import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const SocketContext = createContext(null);

const SOCKET_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.fly8.com';

export const SocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useAuth();

  // Track shown notifications to prevent duplicates
  const shownNotificationsRef = useRef(new Set());

  // Event handlers
  const handleNotification = useCallback(notification => {
    const notificationId = notification.notificationId || notification.id || `${notification.title}-${Date.now()}`;

    // Prevent duplicate toasts
    if (shownNotificationsRef.current.has(notificationId)) {
      return;
    }
    shownNotificationsRef.current.add(notificationId);

    // Clean up old entries after 30 seconds to prevent memory leaks
    setTimeout(() => {
      shownNotificationsRef.current.delete(notificationId);
    }, 30000);

    console.log('New notification:', notification);
    setNotifications(prev => {
      // Avoid duplicates in state
      if (prev.some(n => (n.notificationId || n.id) === notificationId)) {
        return prev;
      }
      return [notification, ...prev];
    });
    setUnreadCount(prev => prev + 1);

    // Show toast notification with unique ID to prevent Sonner duplicates
    toast(notification.title, {
      id: notificationId,
      description: notification.message,
      action: notification.actionUrl
        ? {
            label: 'View',
            onClick: () => (window.location.href = notification.actionUrl),
          }
        : undefined,
    });
  }, []);

  const handleServiceRequestUpdate = useCallback(data => {
    console.log('Service request updated:', data);
    // Dispatch custom event for components to listen
    window.dispatchEvent(
      new CustomEvent('service_request_updated', { detail: data })
    );
  }, []);

  const handleTaskUpdate = useCallback(data => {
    console.log('Task updated:', data);
    window.dispatchEvent(new CustomEvent('task_updated', { detail: data }));
  }, []);

  const handleServiceApplication = useCallback(data => {
    console.log('New service application:', data);
    window.dispatchEvent(
      new CustomEvent('service_application', { detail: data })
    );
  }, []);

  const handleCommissionPaid = useCallback(data => {
    const toastId = `commission-paid-${data.commissionId || data.id || Date.now()}`;
    console.log('Commission paid:', data);
    toast.success('Commission Paid!', {
      id: toastId,
      description: `$${data.amount} has been credited to your account`,
    });
    window.dispatchEvent(new CustomEvent('commission_paid', { detail: data }));
  }, []);

  useEffect(() => {
    if (!user) {
      // Clean up socket when user logs out
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
        setConnected(false);
      }
      return;
    }

    if (socketRef.current) return; // Already connected

    const token = localStorage.getItem('token');
    if (!token) return;

    const newSocket = io(SOCKET_URL, {
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    // Connection events
    newSocket.on('connect', () => {
      console.log('Socket connected');
      setConnected(true);
    });

    newSocket.on('disconnect', reason => {
      console.log('Socket disconnected:', reason);
      setConnected(false);
    });

    newSocket.on('connect_error', error => {
      console.error('Socket connection error:', error.message);
      setConnected(false);
    });

    newSocket.on('reconnect', attemptNumber => {
      console.log('Socket reconnected after', attemptNumber, 'attempts');
      setConnected(true);
    });

    newSocket.on('reconnect_error', error => {
      console.error('Socket reconnection error:', error.message);
    });

    // Server acknowledgment
    newSocket.on('connected', data => {
      console.log('Server acknowledged connection:', data);
    });

    // Notification events
    newSocket.on('new_notification', handleNotification);
    newSocket.on('notification', handleNotification);

    // Service request events
    newSocket.on('service_request_updated', handleServiceRequestUpdate);
    newSocket.on('service_request_created', handleServiceRequestUpdate);
    newSocket.on('service_request_assigned', handleServiceRequestUpdate);

    // Task events
    newSocket.on('task_updated', handleTaskUpdate);
    newSocket.on('task_created', handleTaskUpdate);
    newSocket.on('task_submitted', handleTaskUpdate);
    newSocket.on('task_reviewed', handleTaskUpdate);

    // Application events
    newSocket.on('service_application', handleServiceApplication);

    // Commission events
    newSocket.on('commission_paid', handleCommissionPaid);
    newSocket.on('commission_approved', data => {
      const toastId = `commission-approved-${data.commissionId || data.id || Date.now()}`;
      toast.success('Commission Approved!', {
        id: toastId,
        description: `Your commission of ${data.amount} has been approved`,
      });
    });

    socketRef.current = newSocket;

    return () => {
      newSocket.close();
    };
  }, [
    user,
    handleNotification,
    handleServiceRequestUpdate,
    handleTaskUpdate,
    handleServiceApplication,
    handleCommissionPaid,
  ]);

  // Helper functions
  const joinServiceRequestRoom = serviceRequestId => {
    if (socketRef.current && connected) {
      socketRef.current.emit('join_service_request_room', serviceRequestId);
    }
  };

  const leaveServiceRequestRoom = serviceRequestId => {
    if (socketRef.current && connected) {
      socketRef.current.emit('leave_service_request_room', serviceRequestId);
    }
  };

  const joinStudentRoom = studentId => {
    if (socketRef.current && connected) {
      socketRef.current.emit('join_student_room', studentId);
    }
  };

  const markNotificationRead = notificationId => {
    setNotifications(prev =>
      prev.map(n =>
        n.notificationId === notificationId ? { ...n, isRead: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const value = {
    socket: socketRef.current,
    connected,
    notifications,
    unreadCount,
    setUnreadCount,
    clearNotifications,
    markNotificationRead,
    joinServiceRequestRoom,
    leaveServiceRequestRoom,
    joinStudentRoom,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};
